/**
 * Advisory Session Watcher
 *
 * The single auth-identity watcher that tears down every advisory listener
 * and resets the advisory slice whenever the observed Firebase uid changes
 * (ASVS V3.3 Session Termination). This module exists because this exact
 * gap has shipped twice in this codebase's history — `chat`'s
 * `cleanupAllChatListeners` has zero call sites on `development`, and the
 * abandoned v1 attempt rendered user A's projects to user B.
 *
 * ## Teardown fires from an auth-identity watcher, NOT from `logoutUser.fulfilled`
 *
 * `logoutUser` is dispatched only by an explicit in-tab logout button, so
 * seating teardown inside `logoutUser.fulfilled` cannot cover every
 * sign-out path — token-expiry sign-out and cross-tab sign-out neither
 * dispatch that thunk. Firebase's own auth observer fires for all three
 * (plus the same-tab account switch), so the observer is the only seat
 * that can actually cover every case.
 *
 * ## Exactly one seat — this is intentionally NOT also wired into
 * `userSlice.ts`'s `logoutUser.fulfilled`
 *
 * Firebase's `signOut()` inside `logoutUser` fires this module's own
 * `onAuthStateChanged` observer anyway, so a second call site there would be
 * a second path that can drift from this one. A future contributor
 * "helpfully" adding that second call site should meet
 * `advisorySession.test.ts`'s own source guard, which fails specifically
 * when that reference is added to `userSlice.ts` or `App.tsx`.
 *
 * ## Acyclic import
 *
 * This module is imported BY `store.ts` for the bootstrap call below, so it
 * must never import back from `store.ts` (the same acyclic-import
 * discipline `advisoryThunks.ts` follows). The `dispatch` parameters below
 * are therefore typed as the narrower `Dispatch<AnyAction>` from
 * `@reduxjs/toolkit` (whose `dist/index.d.ts` re-exports `redux`
 * wholesale — `redux` itself is NOT a direct dependency of gchat and must
 * not be imported directly), never as `AppDispatch` from `../../store`.
 * The real application store's `AppDispatch` is assignable to
 * `Dispatch<AnyAction>`, so nothing changes for the real bootstrap call in
 * `store.ts`.
 */

import { onAuthStateChanged } from 'firebase/auth';
import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { auth } from '../../../services/firebaseConfig';
import { clearAdvisorySourcePassageCache } from '../../../services/advisorySourcePassageService';
import { cleanupAllAdvisoryListeners } from './advisoryListeners';
import { resetAdvisory } from './advisorySlice';

/**
 * The only exported teardown path. `cleanupAllAdvisoryListeners()` and
 * `dispatch(resetAdvisory())` are called unconditionally, in that order,
 * every time this function runs. Nothing outside this file calls either
 * half independently — cleanup-called-but-state-never-reset is the EXACT
 * shipped v1 defect (user A's projects rendered to user B), and a
 * listener-count assertion alone would not have caught it. Making the pair
 * inseparable at this export boundary is structural, not a matter of a
 * future caller remembering to do both.
 *
 * Both `cleanupAllAdvisoryListeners()` and `resetAdvisory` are ordinary
 * top-level imports here (not the dynamic `import('./advisorySlice')` the
 * acyclic-import discipline requires of `advisoryThunks.ts`) —
 * `advisorySlice.ts` never imports this module back, so there is no cycle
 * to guard against on that edge.
 */
export function teardownAdvisorySession(dispatch: Dispatch<AnyAction>): void {
  clearAdvisorySourcePassageCache();
  cleanupAllAdvisoryListeners();
  dispatch(resetAdvisory());
}

/**
 * The last uid this watcher observed. Module-scoped (not per-subscription)
 * so it is reachable from `teardownAdvisorySession`'s callers without a
 * class instance, and reset to `null` at the START of every
 * `startAdvisorySessionWatcher` call below — a fresh subscription always
 * begins from a known state, so no test-only reset export is needed.
 */
let lastSeenUid: string | null = null;

/**
 * Subscribes a second, independent `onAuthStateChanged` observer on the
 * shared `auth` instance and tears down the advisory session on any uid
 * change. Started exactly once, from `store.ts`, immediately after
 * `persistor.purge()`.
 *
 * (a) The comparison is a uid CHANGE (`lastSeenUid`, tracked across
 *     calls), covering `uid -> null` (sign-out), `null -> uid` (sign-in) and
 *     `uidA -> uidB` (same-tab account switch). A null-only guard would miss
 *     the same-tab account switch ("switching accounts in the same browser
 *     tab briefly shows the previous user's data").
 * (b) This is a plain module, not a component, specifically so the
 *     teardown behaviour is provable without mounting `App.tsx`'s route
 *     tree, MUI, i18n and `PersistGate` into a data-layer test.
 * (c) Registering a second, independent `onAuthStateChanged` observer
 *     alongside `AuthInitializer`'s existing one (`App.tsx`) is supported
 *     Firebase behaviour, not a conflict — each observer independently
 *     receives every auth-state event on the shared `auth` instance.
 * (d) The very first callback after bootstrap (`null -> uid` at sign-in) is
 *     a harmless no-op teardown: nothing is registered yet and the slice is
 *     already at its initial value, so tearing down changes nothing
 *     observable.
 */
export function startAdvisorySessionWatcher(store: { dispatch: Dispatch<AnyAction> }): () => void {
  lastSeenUid = null;
  return onAuthStateChanged(auth, firebaseUser => {
    const uid = firebaseUser?.uid ?? null;
    if (uid !== lastSeenUid) {
      teardownAdvisorySession(store.dispatch);
      lastSeenUid = uid;
    }
  });
}
